/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { ICheckoutDeliveryOption, ICheckoutDeliveryOptionEdit, ICheckoutDeliveryOptionsError, ICheckoutDeliveryOptionsList, ICheckoutDeliveryOptionsViewProps } from '@msdyn365-commerce-modules/checkout/src/modules/checkout-delivery-options';
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import { DeliveryOption as DeliveryOptionData } from '@msdyn365-commerce/retail-proxy';
import * as React from 'react';
interface ICheckoutDeliveryOptionsListFiltered extends ICheckoutDeliveryOptionsList {
    globalConfig?: {
        [x: string]: unknown;
    };
    options?: DeliveryOptionData[];
}

type ModShipping = {
    id: number;
    amount: number;
}[];

const DeliveryOptionList: React.FC<ICheckoutDeliveryOptionsListFiltered> = ({
    DeliveryOptionsList,
    list,
    globalConfig,
    options
}) => {
    if (!list || list.length === 0) {
        return null;
    }

    const pennyShippingMethodIds = globalConfig?.pennyShippingIds as number[] || [];
    const modShippingMethodIds = globalConfig?.modShippingIds as ModShipping || [];
    let localList: ICheckoutDeliveryOptionEdit[] = [];
    if (options) {
        // maps options with list for sorting
        const combinedList: {option: DeliveryOptionData; list: ICheckoutDeliveryOptionEdit}[] = [];
        options.map((option, index) => combinedList.push({option: option, list: list[index]}));
        // sort list by lowest to highest price
        const combinedListSorted = combinedList.sort((a, b) => (a.option.ShippingChargeAmount! > b.option.ShippingChargeAmount!) ? 1 : ((b.option.ShippingChargeAmount! > a.option.ShippingChargeAmount!) ? -1 : 0));
        combinedListSorted.forEach((deliveryData) => {
            if (deliveryData.list) {
                // search to find out if it is a mod shipping method
                const isModShippingMethod = modShippingMethodIds.find(modShipping => Number(modShipping.id) === deliveryData.option.RecordId);
                // backwards compatibility for penny shipping
                const isPennyShippingMethod = pennyShippingMethodIds.find(pennyShipping => Number(pennyShipping) === deliveryData.option.RecordId);

                // Filter out mod shipping methods that are declared
                // in the global config, only show shipping methods
                // that have a matching mod price for the customer at this current time
                // of checkout. If it's not a mod shipping method, always display
                // it.
                if (isModShippingMethod && deliveryData.option.ShippingChargeAmount === isModShippingMethod.amount) {
                    localList.push(deliveryData.list);
                } else if (isPennyShippingMethod && deliveryData.option.ShippingChargeAmount === 0.01) {
                    localList.push(deliveryData.list);
                } else if (!isModShippingMethod && !isPennyShippingMethod) {
                    localList.push(deliveryData.list);
                }
            }
        });
    }
    // just in case options doesn't load in, fail safely back to the original
    // list
    if (localList.length === 0) { localList = list; }
    return (
        <Node {...DeliveryOptionsList}>
            {localList.map((deliveryOption: ICheckoutDeliveryOptionEdit) => {
                return (<Node {...deliveryOption.DeliveryOption} key={deliveryOption.code}>
                    <label className='ms-checkout-delivery-options__label'>
                        {deliveryOption.radioButton}
                        {deliveryOption.description}
                    </label>
                    {deliveryOption.price}
                </Node>);
            })}
        </Node>
    );
};

const DeliveryOptionSelected: React.FC<ICheckoutDeliveryOption> = ({
    DeliveryOption,
    description,
    price,
}) => {

    return (
        <Node {...DeliveryOption}>
            {description}
            {price}
        </Node>
    );
};

const ErrorMessage: React.FC<ICheckoutDeliveryOptionsError> = ({
    Error,
    title,
    message,
}) => {

    return (
        <Node {...Error}>
            {title}
            {message}
        </Node>
    );
};

const CheckoutDeliveryOptionsView: React.FC<ICheckoutDeliveryOptionsViewProps> = props => {
    const { CheckoutDeliveryOptions,
        viewState,
        deliveryOptions,
        deliveryOptionsData,
        deliveryOptionSelected,
        errorMessage,
        waiting,
        saveButton,
        editButton,
        cancelButton } = props;
    return (
        // TODO: All wrapper div should be provided by viewProps. Once SDK provide the sln, update markup.
        <Module {...CheckoutDeliveryOptions}>
            {viewState.isLoading && waiting}
            {viewState.isError && errorMessage && <ErrorMessage {...errorMessage} />}
            {viewState.isShowList && deliveryOptions && <DeliveryOptionList {...deliveryOptions}  globalConfig={props.context.app.config} options={deliveryOptionsData} />}
            {viewState.isShowSelected && deliveryOptionSelected && <DeliveryOptionSelected {...deliveryOptionSelected} />}
            {viewState.isShowSaveButton && saveButton}
            {viewState.isShowEditButton && editButton}
            {viewState.isShowCancelButton && cancelButton}
        </Module>
    );
};

export default CheckoutDeliveryOptionsView;